import {
  ServiceRegistryFormData,
  useServiceRegistryFormValidation,
} from '@/app/registry/service-registry/_hooks/use-service-form-validation'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { T, useTranslate } from '@tolgee/react'
import React from 'react'
import { useServiceOAuthCallback } from '../_hooks/use-service-oauth-callback'
import {
  ConnectionState,
  SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY,
  ServiceOAuthInitOptions,
  TokenResponse,
} from '../_hooks/use-service-oauth-connection'
import { SERVICE_TYPES } from '../_utils/constants'
import { ServiceRegistryTableDef } from '../tab'
import { ServiceRegistryFormFields } from './form-fields'
import { ServiceRegistryForm } from './service-registry-form'

type ServiceRegistryBaseDialogProps = {
  open: boolean
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>
  isOAuthConnecting: boolean
  onSubmit: (formData: ServiceRegistryFormData) => Promise<void>
  oauthInit: (oauthInitOptions: ServiceOAuthInitOptions) => Promise<void>
  disabled: boolean
  loading: boolean
  connectionState: ConnectionState
  handleOAuthCallback: (code: string, state: string) => Promise<TokenResponse | void>
  clearOAuthState: () => void
  serviceData?: ServiceRegistryTableDef
  isEditMode?: boolean
}

export function ServiceRegistryBaseDialog(props: ServiceRegistryBaseDialogProps) {
  const {
    open,
    onOpenChange,
    isOAuthConnecting,
    onSubmit,
    oauthInit,
    disabled,
    loading,
    connectionState,
    handleOAuthCallback,
    clearOAuthState,
    serviceData,
    isEditMode = false,
  } = props
  const { t } = useTranslate()
  const isOAuthConnected = React.useMemo(
    () => connectionState.status === 'connected',
    [connectionState.status],
  )
  const { validateDialogForm, canOAuthInitConnection } = useServiceRegistryFormValidation({
    isOAuthConnected,
  })
  const { callbackData } = useServiceOAuthCallback({
    handleOAuthCallback,
    clearOAuthState,
  })
  const dialogTKeyPrefix = isEditMode ? 'serviceRegistry.editDialog' : 'serviceRegistry.addDialog'

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <ServiceRegistryForm
          onSubmit={onSubmit}
          oauthCallbackData={callbackData}
          validateDialogForm={validateDialogForm}
          initialFormData={{
            name: serviceData?.name || '',
            url: serviceData?.url || '',
            serviceType: serviceData?.serviceType || SERVICE_TYPES.MCP_OAUTH2,
            key: '',
          }}
        >
          {({ formData, handleSubmit, isFormValid }) => {
            const handleOAuthInit = () => {
              sessionStorage.setItem(
                SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY,
                JSON.stringify(formData),
              )
              oauthInit({ serverUrl: formData?.url })
            }
            const handleCanOAuthInit = React.useCallback(
              () => canOAuthInitConnection({ formData }),
              [formData],
            )
            return (
              <DialogContent
                disabled={disabled}
                aria-describedby={t(`${dialogTKeyPrefix}.aria.describedBy`)}
                className='sm:max-w-md'
              >
                <DialogHeader>
                  <DialogTitle>
                    <T keyName={`${dialogTKeyPrefix}.title`} />
                  </DialogTitle>
                  <DialogDescription className='text-xs italic'>
                    <T keyName='serviceRegistry.dialog.description' />
                  </DialogDescription>
                </DialogHeader>
                <ServiceRegistryFormFields
                  serviceType={formData?.serviceType}
                  disabled={disabled || connectionState.status === 'connected'}
                  oauthInit={handleOAuthInit}
                  canOAuthInit={handleCanOAuthInit}
                  oauthConnectionStatus={connectionState.status}
                  isOAuthConnecting={isOAuthConnecting}
                  bypassVerifyAnimation={!!callbackData?.urlParams?.code}
                />
                <DialogFooter className='gap-2'>
                  <DialogClose asChild>
                    <Button
                      variant='mango-outline'
                      disabled={disabled}
                      title={t('serviceRegistry.dialog.buttons.cancel')}
                    >
                      <T keyName='serviceRegistry.dialog.buttons.cancel' />
                    </Button>
                  </DialogClose>
                  <Button
                    type='submit'
                    onClick={handleSubmit}
                    disabled={!isFormValid || disabled}
                    loading={loading}
                    title={t('serviceRegistry.dialog.buttons.save')}
                  >
                    <T keyName='serviceRegistry.dialog.buttons.save' />
                  </Button>
                </DialogFooter>
              </DialogContent>
            )
          }}
        </ServiceRegistryForm>
      </Dialog>
    </>
  )
}
